import { useReactiveVar } from '@apollo/client';
import React, { FC, Fragment } from 'react';
import { vars } from '../../../reactive';
import { Slot } from '../../../reactive/main';
import { FORM_PAGES } from '../../Form/types';
import FormContainer from '../../Shared/FormContainer/FormContainer';
import { Line } from '../../Shared/Shared';
import { SessionsContainer, SessionsWrapper } from '../styled';
import { FORM_RADIO_COMPONENT_TYPES, FORM_RADIO_TYPES, FormDateAndTime } from '../types';
import FormRadioDate from './Date/FormRadioDate';
import { FormCalendarDateWrapper } from './Date/styled';
import FormRadioDays from './Days/Days';
import FormRadioTime from './Time/FormRadioTime';
import { DateTimeEditContainer, DateTimeEditWrapper, SessionButton } from './styled';
import { Slots } from '../../../views/FormPages/Booking/DateAndTime';

interface FormRadioDateAndTimeProps {
  dateType: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  timeType: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  title?: string;
  formOptions: FormDateAndTime;
  onSubmit: (form: Record<string, Date[][] | Date[]>) => void;
  bookings: Record<string, any>;
  loading: boolean;
  previousPage?: {
    page?: FORM_PAGES;
    animation?: 'fade' | 'fade-out';
    onPrevious?: () => void;
  };
  sessions: number[];
  sessionsState: [number, React.Dispatch<React.SetStateAction<number>>];
  watchedDates: (string | Date)[][] | (string | Date)[];
  days: { day: string; available: number; times: string[] }[];
  selectedSlots: Slot[];
  nextLoading?: boolean;
  orderGroupIndex: number;
  children?: React.ReactNode;
  usedSchedules?: Slots['usedSchedules'];
  busUserTimeType?: string;
  onLoadMore?: () => void;
}

const FormRadioDateAndTime: FC<FormRadioDateAndTimeProps> = ({
  dateType,
  timeType,
  title,
  usedSchedules,
  bookings,
  formOptions,
  onSubmit,
  loading,
  previousPage,
  sessions,
  sessionsState,
  watchedDates,
  days,
  selectedSlots,
  orderGroupIndex,
  nextLoading,
  children,
  busUserTimeType,
  onLoadMore
}) => {
  const { title: defaultTitle } = FORM_RADIO_COMPONENT_TYPES[dateType];
  const { handleSubmit } = formOptions;
  const selectedProduct = useReactiveVar(vars.selectedProduct);
  const [currentSession, setCurrentSession] = sessionsState;
  const isRecurring = dateType === FORM_RADIO_TYPES.DAYS;

  const shouldDisplayTimes = (session: number) => {
    if (currentSession !== session) {
      return false;
    }
    if (dateType === FORM_RADIO_TYPES.DATE) {
      return !!selectedSlots[session]?.date?.fullDate && (selectedSlots[session]?.date?.slots?.length || 0) > 1;
    }

    if (dateType === FORM_RADIO_TYPES.START_DATE) {
      return !!selectedSlots[session]?.startDate?.fullDate && (selectedSlots[session]?.startDate?.slots?.length || 0) > 1;
    }

    return false;
  };

  const completedSessions = selectedSlots.filter(slot => slot.date?.fullDate && slot.time).length;

  const multiDayProduct = selectedProduct?.booking_type === 'MULTI_DAY';

  return (
    <FormCalendarDateWrapper>
      <FormContainer title={title || defaultTitle} onSubmit={handleSubmit(onSubmit)} previousPage={previousPage} stickyNextButton={multiDayProduct} loading={nextLoading}>
        {children}
        {sessions.length > 1 && (
          <SessionsWrapper>
            <SessionsContainer>
              {sessions.map(session => {
                const selected = currentSession === session;
                const sessionDates = selectedSlots?.[session];
                const completed = !!(sessionDates?.date?.fullDate && sessionDates?.time);
                const disabled = loading || completedSessions < session;
                return (
                  <SessionButton key={session} onClick={() => setCurrentSession(session)} type="button" selected={selected} completed={completed} disabled={disabled}>
                    <span>Session {session + 1}</span>
                  </SessionButton>
                );
              })}
            </SessionsContainer>
            <Line />
          </SessionsWrapper>
        )}

        {sessions.map(
          session =>
            currentSession === session && (
              <Fragment key={session}>
                {!isRecurring && (
                  <FormRadioDate
                    type={dateType}
                    bookings={bookings}
                    formOptions={formOptions}
                    session={session}
                    loading={loading}
                    watchedDates={watchedDates}
                    orderGroupIndex={orderGroupIndex}
                    onLoadMore={onLoadMore}
                  />
                )}

                {isRecurring && <FormRadioDays formOptions={formOptions} session={session} type={dateType} days={days} loading={loading} />}

                {!loading && (
                  <DateTimeEditWrapper shouldDisplay={shouldDisplayTimes(session)}>
                    {shouldDisplayTimes(session) && (
                      <DateTimeEditContainer>
                        <Line />

                        <FormRadioTime
                          formOptions={formOptions}
                          sessionIndex={session}
                          type={timeType}
                          secondaryType={busUserTimeType}
                          selectedSlot={selectedSlots?.[currentSession] || {}}
                          orderGroupIndex={orderGroupIndex}
                          key={JSON.stringify(selectedSlots)}
                          usedSchedules={usedSchedules}
                        />
                      </DateTimeEditContainer>
                    )}
                  </DateTimeEditWrapper>
                )}
              </Fragment>
            )
        )}
      </FormContainer>
    </FormCalendarDateWrapper>
  );
};

export default FormRadioDateAndTime;
